import { EChartsOption } from 'echarts';
import { translateComponent } from 'i18n';
import { COLOR_GRADIENTS, PaletteName } from 'styles/theme';

type VisualMapSettingsT = {
    useGradientColor: boolean;
    reverseGradientColor: boolean;
    minValue?: number;
    maxValue?: number;
    showLegend?: boolean;
    showLegendOnRightSide?: boolean;
    palette: PaletteName;
    dimensionKey?: string | number;
    useDataset?: boolean;
    formatter: (value: number) => string;
};

/**
 * This helpers builds the echart visual map settings.
 * @param {VisualMapSettingsT} - The full set of visuela map parameters.
 * @returns {EChartsOption['visualMap']} - The visual map settings for echarts.
 */
export const getVisualMapSettings = ({
    useGradientColor,
    reverseGradientColor,
    minValue,
    maxValue,
    showLegend,
    showLegendOnRightSide,
    palette,
    formatter,
    dimensionKey,
    useDataset,
}: VisualMapSettingsT): EChartsOption['visualMap'] => {
    if (!useGradientColor) {
        return undefined;
    }
    const paletteGradient = COLOR_GRADIENTS[palette];
    const color = reverseGradientColor
        ? paletteGradient.slice().reverse()
        : paletteGradient;

    // @ts-expect-error the EChart library does not type the serieIndex/dimension property properly.
    // It should be an optional number or string, but it is typed as an optional number.
    // TODO: Fix this when the EChart library is updated (not fixed in the 5.4.2).
    return {
        show: showLegend,
        orient: showLegendOnRightSide ? 'vertical' : 'horizontal',
        left: showLegendOnRightSide ? 'right' : 'center',
        top: showLegendOnRightSide ? 'middle' : 'auto',
        min: minValue ?? 0,
        max: maxValue ?? 1,
        ...(useDataset ? { dimension: dimensionKey } : { serieIndex: 0 }),
        text: [
            translateComponent('chartSettings.gradient.max'),
            translateComponent('chartSettings.gradient.min'),
        ],
        // Map the score column to color
        inRange: {
            color,
        },
        formatter,
    };
};
